import { useTranslation } from 'react-i18next'

import {
  CampaignCard,
  CampaignCardBody,
  CampaignCardHeader,
} from '#/entities/campaign'
import type { CampaignReplyBucket } from '#/entities/campaign'

interface CampaignReplyTimingCardProps {
  buckets: CampaignReplyBucket[]
  note: string
}

/** Height of the tallest column. Every share is expressed against it. */
const CHART_HEIGHT = 'h-17.75'

function Legend() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3.5">
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full bg-campaign-reply-early"
        />
        {t('campaigns.detail.step.title', { position: 1 })}
      </span>
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full bg-campaign-reply-late"
        />
        {t('campaigns.detail.step.title', { position: 2 })}
      </span>
    </div>
  )
}

/**
 * When replies landed, per step. The columns are bottom-aligned stacks rather
 * than a real chart library — eight buckets and two series do not earn one, and
 * the shares arrive pre-computed.
 */
export function CampaignReplyTimingCard({
  buckets,
  note,
}: CampaignReplyTimingCardProps) {
  const { t } = useTranslation()
  return (
    <CampaignCard>
      <CampaignCardHeader
        label={t('campaigns.detail.sections.replyTiming')}
        action={<Legend />}
      />
      <CampaignCardBody>
        {/* `items-stretch` is load-bearing: the bars size themselves as a
            percentage of the column, so each column has to inherit the chart's
            full height rather than shrink to its content. */}
        <div className={`flex items-stretch gap-2 ${CHART_HEIGHT}`}>
          {buckets.map((bucket) => (
            <div
              key={bucket.key}
              className="flex flex-1 flex-col justify-end gap-0.5"
            >
              <span
                aria-hidden="true"
                className="rounded-sm bg-campaign-reply-early"
                style={{ height: `${bucket.early * 100}%` }}
              />
              <span
                aria-hidden="true"
                className="rounded-sm bg-campaign-reply-late"
                style={{ height: `${bucket.late * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {buckets.map((bucket) => (
            <span
              key={bucket.key}
              className="flex-1 text-center text-2xs text-gray-300"
            >
              {bucket.label}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-500">{note}</p>
      </CampaignCardBody>
    </CampaignCard>
  )
}
